import type { ethers } from "ethers";
import { assertFreshWalletScreening } from "../utils/wallet-screening";
import {
  markFlatJoinSplitV2SettlementWalletRejected,
  type FlatJoinSplitV2JournalScope,
} from "./pendingJournal";

interface JournaledWalletSubmission {
  journal: { id: string; scope: FlatJoinSplitV2JournalScope };
  signal?: AbortSignal;
  onArchiveError?: (error: unknown) => void;
}

/** Add recovery context without mutating provider-owned (possibly frozen) errors. */
export function walletSettlementErrorWithContext(
  error: unknown,
  journalId: string,
  submittedHash?: string,
): Error & { journalId: string; txHash?: string } {
  const original = error && typeof error === "object" ? error : undefined;
  const details: Record<string, unknown> = {};
  // Copy data properties only, not arbitrary accessors. Error message/stack
  // are usually non-enumerable and must survive wrapping too.
  for (const [key, descriptor] of Object.entries(
    Object.getOwnPropertyDescriptors(original ?? {}),
  )) {
    if ("value" in descriptor && key !== "__proto__") {
      Object.defineProperty(details, key, {
        value: descriptor.value,
        enumerable: true,
      });
    }
  }
  const failure = Object.assign(
    new Error(
      typeof details.message === "string" ? details.message : String(error),
    ),
    details,
    {
      name:
        typeof details.name === "string"
          ? details.name
          : error instanceof DOMException
            ? error.name
            : "Error",
      cause: error,
      journalId,
      txHash:
        typeof details.txHash === "string" ? details.txHash : submittedHash,
    },
  );
  return failure;
}

function isExplicitWalletRejection(error: unknown): boolean {
  if (!error || typeof error !== "object") return false;
  const failure = error as {
    code?: unknown;
    action?: unknown;
    hash?: unknown;
    txHash?: unknown;
    transactionHash?: unknown;
    receipt?: unknown;
    replacement?: unknown;
    transaction?: { hash?: unknown };
  };
  return (
    (failure.code === "ACTION_REJECTED" || failure.code === 4001) &&
    (failure.action === undefined || failure.action === "sendTransaction") &&
    failure.hash === undefined &&
    failure.txHash === undefined &&
    failure.transactionHash === undefined &&
    failure.receipt === undefined &&
    failure.replacement === undefined &&
    failure.transaction?.hash === undefined
  );
}

async function archiveUnsubmitted(
  params: JournaledWalletSubmission,
): Promise<void> {
  try {
    await markFlatJoinSplitV2SettlementWalletRejected(
      params.journal.scope,
      params.journal.id,
    );
  } catch (error) {
    // Failed/quota-limited writes leave the old active record intact. Never
    // remove recovery secrets or let cleanup replace the original failure.
    try {
      params.onArchiveError?.(error);
    } catch {
      // Logging is advisory and cannot replace the wallet's original failure.
    }
  }
}

/** Only call before attempting submission, while the wallet lease is held. */
export async function assertJournaledWalletSubmissionNotAborted(
  params: JournaledWalletSubmission,
): Promise<void> {
  if (!params.signal?.aborted) return;
  await archiveUnsubmitted(params);
  throw Object.assign(
    new DOMException(
      "Wallet settlement was cancelled before submission",
      "AbortError",
    ),
    {
      journalId: params.journal.id,
    },
  );
}

/** Share the precise wallet-send boundary across deposits and private spends. */
export async function sendJournaledWalletTransaction(
  params: JournaledWalletSubmission & {
    signer: Pick<ethers.Signer, "sendTransaction">;
    transaction: ethers.TransactionRequest;
    onWalletRequest?: () => void;
  },
): Promise<ethers.TransactionResponse> {
  await assertFreshWalletScreening(
    params.journal.scope.accountAddress,
    params.journal.scope.chainId,
    params.signal,
  );
  // No asynchronous gap between the final abort check and invoking the wallet.
  if (params.signal?.aborted)
    await assertJournaledWalletSubmissionNotAborted(params);
  try {
    params.onWalletRequest?.();
    return await params.signer.sendTransaction(params.transaction);
  } catch (error) {
    // Never apply this classification to relayer or post-submission errors.
    if (isExplicitWalletRejection(error)) await archiveUnsubmitted(params);
    throw walletSettlementErrorWithContext(error, params.journal.id);
  }
}
